import { useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { toast } from "sonner";
import { LifeBuoy, ChevronDown, ChevronRight, Check, RotateCcw, Bot, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { GET_SUPPORT_REQUESTS } from "@/graphql/queries/support";
import { RESOLVE_SUPPORT_REQUEST } from "@/graphql/mutations/support";

/**
 * /admin/support — „Parašyk mums" as a queue.
 *
 * Newest activity first, and that is the whole ordering: unlike the content
 * queue there is nothing to weigh, because every row is a person waiting for an
 * answer. What a row is worth is how long they have waited.
 *
 * Resolving keeps the row. A queue that erases what it answered cannot say what
 * it answered — the rule `ResolveContentFlag` follows.
 */

type Request = {
  id: string;
  category: string;
  categoryLabel: string;
  summary: string;
  status: string;
  resolved: boolean;
  filedBy: string;
  reportedByRole: string;
  occurrences: number;
  firstSeenAt: string;
  lastSeenAt: string;
  transcript: { role: string; text: string }[];
  requestContext: Record<string, string>;
  studentName: string | null;
  studentId: string;
};

const ROLE_LABELS: Record<string, string> = {
  student: "mokinys",
  guardian: "globėjas",
  admin: "administratorius",
};

function ago(iso: string) {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 60) return `prieš ${Math.max(1, minutes)} min.`;
  if (minutes < 60 * 24) return `prieš ${Math.round(minutes / 60)} val.`;
  return `prieš ${Math.round(minutes / (60 * 24))} d.`;
}

export default function AdminSupportPage() {
  const [status, setStatus] = useState("open");
  const [openId, setOpenId] = useState<string | null>(null);

  const { data, loading, refetch } = useQuery(GET_SUPPORT_REQUESTS, {
    variables: { status },
    fetchPolicy: "cache-and-network",
  });
  const [resolveRequest] = useMutation(RESOLVE_SUPPORT_REQUEST);

  const requests: Request[] = (data as any)?.supportRequests ?? [];
  const open = (data as any)?.openSupportRequests ?? 0;

  async function toggle(request: Request) {
    const result: any = await resolveRequest({
      variables: { id: request.id, resolved: !request.resolved },
    });
    const errors = result?.data?.resolveSupportRequest?.errors ?? [];
    if (errors.length) {
      toast.error(errors[0]);
      return;
    }
    toast.success(request.resolved ? "Grąžinta į sąrašą" : "Pažymėta kaip atsakyta");
    refetch();
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <LifeBuoy className="h-6 w-6 text-primary" />
          Pagalbos užklausos
        </h1>
        <Badge variant={open > 0 ? "default" : "secondary"}>{open} neatsakytos</Badge>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="ml-auto h-9 w-[13rem]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Neatsakytos</SelectItem>
            <SelectItem value="resolved">Atsakytos</SelectItem>
            <SelectItem value="all">Visos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading && requests.length === 0 && <p className="text-muted-foreground">Kraunama...</p>}

      {!loading && requests.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Nieko nėra — niekas nieko neklausė.
          </CardContent>
        </Card>
      )}

      <div className="flex flex-col gap-3">
        {requests.map((request) => {
          const expanded = openId === request.id;
          return (
            <Card key={request.id}>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-start gap-2">
                  <button
                    type="button"
                    onClick={() => setOpenId(expanded ? null : request.id)}
                    className="flex flex-1 items-start gap-2 text-left cursor-pointer"
                  >
                    {expanded ? (
                      <ChevronDown className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <div>
                      <CardTitle className="text-base font-medium">{request.summary}</CardTitle>
                      <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                        <span>{request.categoryLabel}</span>
                        <span>·</span>
                        <span>
                          {request.studentName ?? request.studentId} (
                          {ROLE_LABELS[request.reportedByRole] ?? request.reportedByRole})
                        </span>
                        <span>·</span>
                        <span>{ago(request.lastSeenAt)}</span>
                        {request.occurrences > 1 && (
                          <>
                            <span>·</span>
                            <span className="font-medium">{request.occurrences}× kartojosi</span>
                          </>
                        )}
                      </p>
                    </div>
                  </button>

                  {/* Who decided this was worth filing. Worth showing: an
                      AI-filed row may be the model being eager, where somebody
                      pressing the button never is. */}
                  <Badge variant="outline" className="gap-1">
                    {request.filedBy === "ai" ? (
                      <>
                        <Bot className="h-3 w-3" /> AI
                      </>
                    ) : (
                      <>
                        <User className="h-3 w-3" /> pats
                      </>
                    )}
                  </Badge>

                  <Button size="sm" variant={request.resolved ? "outline" : "default"} onClick={() => toggle(request)}>
                    {request.resolved ? (
                      <>
                        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Atidaryti
                      </>
                    ) : (
                      <>
                        <Check className="mr-1 h-3.5 w-3.5" /> Atsakyta
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>

              {expanded && (
                <CardContent className="flex flex-col gap-3 pt-0">
                  {/* The person's own words — the one thing no summary replaces. */}
                  <div className="flex flex-col gap-2 rounded-lg bg-muted/40 p-3">
                    {request.transcript.length === 0 && (
                      <p className="text-sm text-muted-foreground">Pokalbio nėra.</p>
                    )}
                    {request.transcript.map((turn, i) => (
                      <div key={i} className="text-sm">
                        <span className="font-medium text-muted-foreground">
                          {turn.role === "user" ? "Žmogus: " : "AI: "}
                        </span>
                        {turn.text}
                      </div>
                    ))}
                  </div>

                  {/* Auto-attached context. `newestError` is the useful one: it
                      joins straight onto /admin/errors, where the backtrace is. */}
                  {Object.keys(request.requestContext).length > 0 && (
                    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                      {Object.entries(request.requestContext).map(([key, value]) => (
                        <div key={key} className="col-span-2 grid grid-cols-subgrid">
                          <dt className="font-medium text-muted-foreground">{key}</dt>
                          <dd className="break-all font-mono">{String(value)}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
